import type { ClientHints } from "../types";
import { safeAsync } from "../utils/safe";

interface UADataValues {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  platformVersion?: string;
  architecture?: string;
  bitness?: string;
  model?: string;
  uaFullVersion?: string;
  fullVersionList?: { brand: string; version: string }[];
  wow64?: boolean;
}

interface NavigatorUAData {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?(hints: string[]): Promise<UADataValues>;
}

/**
 * UA Client Hints (high-entropy). Chromium-only; returns null elsewhere.
 * High-entropy values require an async call and user-agent reduction makes the
 * low-entropy `brands` deliberately coarse, so we always ask for the full list.
 */
export async function collectClientHints(): Promise<ClientHints | null> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData) return null;

  const high = await safeAsync<UADataValues>(
    () =>
      uaData.getHighEntropyValues?.([
        "architecture",
        "bitness",
        "model",
        "platformVersion",
        "uaFullVersion",
        "fullVersionList",
        "wow64",
      ]) ?? Promise.resolve({}),
    {}
  );

  return {
    brands: high.brands ?? uaData.brands ?? [],
    mobile: high.mobile ?? uaData.mobile ?? null,
    platform: high.platform ?? uaData.platform ?? null,
    platformVersion: high.platformVersion ?? null,
    architecture: high.architecture ?? null,
    bitness: high.bitness ?? null,
    model: high.model ?? null,
    uaFullVersion: high.uaFullVersion ?? null,
    fullVersionList: high.fullVersionList ?? [],
    wow64: high.wow64 ?? null,
  };
}
